import { ConfirmDialog, isolate } from '@batiflow/ui';
import { Button, MessageBar, MessageBarBody, __css, tokens } from '@fluentui/react-components';
import { PersonAdd20Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { isClosed, isStepUpRequired, type Space } from '../model';
import { AddDelegateDialog } from './add-delegate-dialog';
import { usePartnerships, useRemovePartner } from './api';
import { MembersPanel } from './members-panel';
import { sharingErrorMessage } from './model';
import { SharingSection } from './sharing-ui';

const useStyles = __css({"usage":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"f19n0e5"},"leave":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"}});

/**
 * The "Gérer mes délégués" tab of a partner admin: the people of its organisation who reach the
 * space (the partnership's cap usage included), "Ajouter un membre" within the partnership's scope
 * and rights, and "Quitter l'espace" to end the partnership from its side.
 */
export function SpaceDelegatesTab({ space }: { space: Space }) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const navigate = useNavigate();
  const notify = useNotify();
  const closed = isClosed(space);
  const { partnerId, organizationId } = space.access;
  const partnerships = usePartnerships(organizationId ?? '', organizationId !== null);
  const removePartner = useRemovePartner(space.id);
  const reportError = (error: unknown) => {
    if (isStepUpRequired(error)) return;
    notify({
      intent: 'error',
      message: sharingErrorMessage(t, tCommon, error, { forbidden: 'sharing.errors.forbidden' }),
    });
  };
  const [adding, setAdding] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const partnership = partnerships.data?.items.find((item) => item.id === partnerId);
  const full = partnership ? partnership.activeMembers >= partnership.memberCap : false;
  const canAdd = !closed && organizationId !== null && partnership?.status === 'active' && !full;

  return (
    <>
      <SharingSection
        id="delegates-list"
        title={t('sharing.delegates.heading')}
        description={t('sharing.delegates.description')}
        actions={
          canAdd ? (
            <Button
              appearance="primary"
              icon={<PersonAdd20Regular />}
              onClick={() => {
                setAdding(true);
              }}
            >
              {t('sharing.delegates.add.open')}
            </Button>
          ) : null
        }
      >
        {partnership ? (
          <p className={styles.usage}>
            {t('sharing.delegates.usage', {
              active: partnership.activeMembers,
              cap: partnership.memberCap,
            })}
          </p>
        ) : null}
        {full && !closed ? (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>{t('sharing.delegates.capReached')}</MessageBarBody>
          </MessageBar>
        ) : null}
        <MembersPanel
          space={space}
          {...(partnerId ? { partnerId } : {})}
          allowExtend={false}
          showOrganisation={false}
          emptyTitle={t('sharing.delegates.emptyTitle')}
          emptyBody={t('sharing.delegates.emptyBody')}
        />
      </SharingSection>

      {closed || partnerId === null ? null : (
        <SharingSection
          id="leave-space"
          title={t('sharing.delegates.leave.heading')}
          description={t('sharing.delegates.leave.description')}
        >
          <div className={styles.leave}>
            <div>
              <Button
                appearance="secondary"
                onClick={() => {
                  setLeaving(true);
                }}
              >
                {t('sharing.delegates.leave.button')}
              </Button>
            </div>
          </div>
        </SharingSection>
      )}

      {partnership && organizationId ? (
        <AddDelegateDialog
          spaceId={space.id}
          orgId={organizationId}
          partnership={partnership}
          open={adding}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={leaving}
        destructive
        title={t('sharing.delegates.leave.title', { name: isolate(space.name) })}
        body={t('sharing.delegates.leave.body', { host: isolate(space.host.name) })}
        confirmLabel={t('sharing.delegates.leave.button')}
        onOpenChange={setLeaving}
        onConfirm={async () => {
          if (partnerId === null) return;
          await removePartner.mutateAsync({ spaceId: space.id, partnerId });
          notify({ intent: 'success', message: t('sharing.delegates.leave.done') });
          void navigate({ to: '/spaces' });
        }}
        onError={reportError}
      />
    </>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/delegates-tab.griffel.css";